import { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useGuideActivation } from '@/hooks/app/useGuideActivation';
import useProgramStats from '@/hooks/app/useProgramStats';
import { useUserState } from '@/hooks/store';
import { getPlayTarget, isGuideLocked, isGuidePlayable } from '@/utils/app/guideRowRules';

import messages from './messages';
import { Button, PauseGlyph, PlayGlyph } from './styles';
import { ProgramPlayButtonProps } from './types';

const ProgramPlayButton = ({ program }: ProgramPlayButtonProps): ReactElement | null => {
	const { formatMessage } = useIntl();
	const user = useUserState();
	const { data } = useProgramStats(program);
	const progress = data ?? 0;
	const { guide, isResuming } = getPlayTarget(program, progress);

	const { activate, isLive, playerMode } = useGuideActivation(guide, program, {
		isLocked: guide ? isGuideLocked(guide, Boolean(user?.subscription?.valid)) : false,
		isDisabled: guide ? !isGuidePlayable(guide, program, progress) : false,
	});

	if (!guide) {
		return null;
	}

	const isPlaying = isLive && playerMode === 'playing';
	const label = formatMessage(isPlaying ? messages.pause : isResuming ? messages.playNext : messages.play);

	return (
		<Button type="button" onClick={activate} data-testid="program-play-button">
			{isPlaying ? <PauseGlyph aria-hidden /> : <PlayGlyph aria-hidden />}
			{label}
		</Button>
	);
};

export default ProgramPlayButton;
